import { Injectable, Output, EventEmitter } from '@angular/core';
import { HttpService } from 'src/app/core/data/service/http.service';
import { LoginRequest, RegisterRequest } from 'src/app/data/schema/auth-models';
import { ISocialLoginRequest, IResetPasswordRequest, IActivateRequest, ILoginResponse } from 'src/app/data/schema/interfaces';
import { Profile } from 'src/app/data/schema/profile';
import { HttpClient } from '@angular/common/http';
import { IdleTimer } from 'src/app/shared/helpers/idle-timer';
import { RRConfiguration } from 'src/app/data/schema/rr-configuration';
import { AppSettings } from 'src/app/core/configs/app.settings';
import { ServiceHelper } from 'src/app/core/helper/services.helper';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService extends HttpService {

  @Output() 'loginStateChanged': EventEmitter<number> = new EventEmitter();

  private jwt: string;

  private userInfo: Profile = new Profile();

  constructor(http: HttpClient, private idleTimer: IdleTimer) {
    super(http);
    this.endpoint = `${this.apiURL}/auth`;
    this.autoLogin();
    // Log.debug('Auth Service Made');
  }

  get token() { return this.jwt; }
  get profile() { return this.userInfo; }
  get isSuper() { return this.userInfo != null && this.userInfo.roles.includes('SuperAdmin'); }

  public async startIdleTimer() {
    try {
      // get the config value for the timeout
      const timeout = await RRConfiguration.getByKey('idle_timeout');
      if (timeout) {
        this.idleTimer.start(+timeout.configurationValue);
      }
    } catch (error) {
      console.error('Error getting config', error);
      // start the idle timer with a default of 15 minutes
      this.idleTimer.start();
    }
  }

  autoLogin() {
    const authStr = localStorage.getItem('rr-auth') || '{}';
    const authResult = JSON.parse(authStr);
    // Test if we have a token and if the expiration > now
    if (authResult.token && new Date(authResult.tokenExpires) > new Date()) {
      this.storeLoginResult(authResult);
      this.fetchProfile();
      // Log.debug('Auto-login successful.');
    } else {
      // Log.debug('Auto-login failed. No or Expired token.');
    }
  }

  isLoginValid() {
    const authStr = localStorage.getItem('rr-auth') || '{}';
    const authResult = JSON.parse(authStr);

    return (authResult.token && new Date(authResult.tokenExpires) > new Date()) ? true : false;
  }

 async logout(emit: boolean = true) {
    if (emit) {
      this.loginStateChanged.emit(0);
    }
    // if logged out stop the idle timer
    this.idleTimer.stop();

    if (await ServiceHelper.modalCtrl.getTop() != null) {
      await ServiceHelper.modalCtrl.dismiss();
    }

    localStorage.removeItem('rr-auth');
    localStorage.removeItem('cbsjwt');
    this.jwt = undefined;
    this.userInfo = new Profile();
  }

  async login(details: LoginRequest) {
    try {
      return await this.runLogin(details, `${this.endpoint}/login`);
    } catch (error) {
      console.error('Login error: ' + error);
      throw error;
    }
  }

  async socialLogin(details: ISocialLoginRequest) {
    try {
      return await this.runLogin(details, `${this.endpoint}/login/${details.provider}`);
    } catch (error) {
      console.error('Error social login', error);
      throw error || 'Error logging in.';
    }
  }

  async tokenLogin(cbsToken: string) {
    try {
      const rslt = await this.runLogin({ token: cbsToken, provider: 'CBS' }, `${this.endpoint}/cbs`);
      return rslt;
    } catch (error) {
      console.error('Login error: ' + error);
      throw error;
    }
  }

  async register(profile: RegisterRequest) {
    try {
      // Run the register request, posting the data
      return await this.post(profile, `${this.endpoint}/register`);
    } catch (error) {
      console.error('Error register', error);
      throw error || 'Error registering account.';
    }
  }

  async resendActivation(id: string) {
    try {
      // Run the resend activation, posting the data
      await this.post(JSON.stringify(id), `${this.endpoint}/activation/send`, 'text');
    } catch (error) {
      console.error('Error resent activation', error);
      throw error || 'Error requesting activation email.';
    }
  }

  async requestReset(email: string) {
    try {
      // Run the request reset, posting the data
      await this.post(JSON.stringify(email), `${this.endpoint}/forgot`, 'text');
    } catch (error) {
      console.error('Error request reset', error);
      throw error || 'Error requesting password reset.';
    }
  }

  async resetPassword(request: IResetPasswordRequest) {
    try {
      // Run the reset request, posting the data
      await this.post(request, `${this.endpoint}/passwordreset`, 'text');
    } catch (error) {
      console.error('Error reset pwd', error);
      throw error || 'Error resetting password.';
    }
  }

  async activateAccount(request: IActivateRequest) {
    try {
      // Run the activate request, posting the data
      return (await this.post(request, `${this.endpoint}/activate`) as string);
    } catch (error) {
      console.error('Error activating', error);
      throw error || 'Error activating account.';
    }
  }

  async getCBSToken(raffleId: number) {
    try {
      let url = `${AppSettings.cbsAPIUrl}/auth/sso/generate/rafflerocket`;
      if (raffleId) {
        url += `/${raffleId}`;
      }
      return await this.fetch(url, 'text');
    } catch (error) {
      console.error('Error getting CBS SSO token', error);
      throw error || 'Error getting CBS SSO token.';
    }
  }

  updateProfile(profile: Profile) {
    this.userInfo = profile;
    // Load the stored result. Replace the profile, and save it again
    const authStr = localStorage.getItem('rr-auth') || '{}';
    const authResult = JSON.parse(authStr);
    authResult.profile = profile;
    localStorage.setItem('rr-auth', JSON.stringify(authResult));
  }

  protected getUpdateUrl(entity: any): string {
    throw new Error('Method not implemented.');
  }

  protected isUpdate(entity: any): boolean {
    throw new Error('Method not implemented.');
  }

  private async runLogin(data: LoginRequest | ISocialLoginRequest, endpoint: string = this.endpoint) {
    try {
      // Run the login request, posting the data
      const loginResp = await this.post(data, endpoint) as ILoginResponse;
      this.storeLoginResult(loginResp);
    } catch (error) {
      console.error('Error during login', error);
      throw error || 'Error during login.';
    }
  }

  private storeLoginResult(result: ILoginResponse) {
    // set local properties
    this.jwt = result.token;
    this.userInfo = result.profile;
    // set the storage
    localStorage.setItem('rr-auth', JSON.stringify(result));
    this.loginStateChanged.emit(1);

    // send the session to the server
    this.post(null, `${this.apiURL}/sessions`);
  }


  private async fetchProfile() {
    try {
      const profile = new Profile(await this.fetch(`${this.apiURL}/users/profile`));
      this.updateProfile(profile);
    } catch (error) {
      console.error('Error fetching user profile. Error:', error);
    }
  }

}
